import { types, applySnapshot, getSnapshot, flow } from 'mobx-state-tree';
import { reaction } from 'mobx';
import generateUUID from 'uuid/v1';
import { castEventToValue } from 'js/services/FormTools';
import GooglePlaces from 'js/services/GooglePlaces';

export default types
  .model('Address', {
    uuid: types.optional(types.string, generateUUID),
    id: 0,
    formatted_address: types.maybeNull(types.string),
    address1: types.maybeNull(types.string),
    address2: types.maybeNull(types.string),
    city: types.maybeNull(types.string),
    state: types.maybeNull(types.string),
    zip: types.maybeNull(types.string),
    country: types.maybeNull(types.string),
    lat: '0',
    lng: '0',
    timezone: types.optional(types.union(types.number, types.string), () => new Date().getTimezoneOffset() / 60),
    loadTimezone: false,
  })
  .views(self => ({
    get hasAddress() {
      return !!(self.address1 && self.formatted_address && self.zip);
    },
    get summary() {
      let address = self.address1;
      if (self.address2) {
        address += `, ${self.address2},`;
      }
      address += ` ${self.city}, ${self.state} ${self.zip}`;
      return address;
    },
  }))
  .actions(self => {
    let timezoneReaction;
    let reloadAddressData;

    return {
      afterCreate() {
        reloadAddressData = reaction(
          () => self.timezone === 'US/Central',
          invalidTimezone => {
            if (invalidTimezone && self.formatted_address.length) {
              self.refreshAddress();
              reloadAddressData();
            }
          },
          { fireImmediately: true },
        );

        timezoneReaction = reaction(
          () => self.loadTimezone,
          loadTimezone => {
            if (loadTimezone && self.lat.length > 1 && self.lng.length > 1) {
              self.loadTimezoneNow();
            }
          },
        );
      },

      beforeDestroy() {
        timezoneReaction();
        reloadAddressData();
      },

      loadTimezoneNow: flow(function* loadTimezoneNow() {
        try {
          self.loadTimezone = false;
          self.timezone = yield GooglePlaces.locationTimezone(self.lat, self.lng);
          window.app.env !== 'production' && console.log('Address timezone', self.timezone);
        } catch (error) {
          window.app.env !== 'production' && console.error('Error loading timezone: ', error);
        }
      }),

      refreshAddress: flow(function* refreshAddress() {
        try {
          const suggestions = yield GooglePlaces.getSuggestions(self.formatted_address);
          if (suggestions.length) {
            const address = yield GooglePlaces.placeAddress(suggestions[0].place_id);
            self.lat = address.lat;
            self.lng = address.lng;
            yield self.loadTimezoneNow();
          }
        } catch (error) {
          window.app.env !== 'production' && console.error("Error refreshing address:", error);
        }
      }),

      clearAddress() {
        applySnapshot(self, {});
        return self;
      },
      setAddress(address) {
        applySnapshot(self, address ? { ...getSnapshot(self), ...address } : {});
        return self;
      },
      setAddress2(address2) {
        self.address2 = castEventToValue(address2);
        return self;
      },
    };
  });
